import { useEffect, useState } from "react";
import { MailCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function EmailVerificationStep({ email, onVerified }) {
  const { verifyEmail, resendVerification } = useAuth();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [resent, setResent] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await verifyEmail(code.trim());
      onVerified();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setError("");
    setResent(false);
    try {
      await resendVerification();
      setCooldown(60);
      setResent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="bg-white rounded-2xl border p-6 shadow-sm space-y-4" data-testid="email-verification-step">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-emerald-900 flex items-center justify-center shrink-0">
          <MailCheck className="w-5 h-5 text-amber-400" />
        </div>
        <div>
          <h2 className="font-bold text-emerald-950">تأكيد البريد الإلكتروني</h2>
          <p className="text-xs text-slate-500 leading-5">
            أرسلنا رمز تحقق من 6 أرقام إلى <b dir="ltr">{email}</b> — أدخله لإكمال تفعيل اشتراكك
          </p>
        </div>
      </div>
      <form onSubmit={submit} className="space-y-3">
        <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          dir="ltr" inputMode="numeric" autoComplete="one-time-code"
          data-testid="verify-code-input" placeholder="——————" required
          className="h-12 text-center text-xl font-bold tracking-[0.5em]" />
        {error && (
          <div data-testid="verify-error-alert"
            className="text-sm bg-rose-50 text-rose-700 border border-rose-200 rounded-lg px-3 py-2">
            {error}
          </div>
        )}
        <Button type="submit" data-testid="verify-submit-button" disabled={loading || code.length !== 6}
          className="w-full bg-emerald-900 hover:bg-emerald-950 text-white h-11">
          {loading ? "جارٍ التحقق..." : "تأكيد الرمز وتفعيل الاشتراك"}
        </Button>
      </form>
      <p className="text-center text-xs text-slate-500">
        لم يصلك الرمز؟{" "}
        <button type="button" onClick={resend} disabled={cooldown > 0}
          data-testid="resend-code-button"
          className="text-emerald-800 font-semibold hover:underline disabled:text-slate-400 disabled:no-underline">
          {cooldown > 0 ? `إعادة الإرسال بعد ${cooldown} ثانية` : "إعادة إرسال الرمز"}
        </button>
      </p>
      {resent && cooldown > 0 && (
        <p className="text-center text-[11px] text-emerald-700" data-testid="resend-success-note">
          تم إرسال رمز جديد — الرمز صالح لمدة 10 دقائق
        </p>
      )}
    </div>
  );
}
